/**
 * droneOpsApi.js v4
 * droneApi.list() は既存の /devices API（IoT Thing一覧）を使用
 */
import { getSession, signOut } from './auth';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

async function authFetch(path, options = {}) {
  let session;
  try {
    session = await getSession();
  } catch (err) {
    signOut();
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: session.idToken,
    ...options.headers,
  };
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    signOut();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || `API Error: ${response.status}`);
  return data;
}

// ============================================================
// 正規化ヘルパー
// ============================================================

/**
 * IoT Device（/devices レスポンス）を FlightCalendar 用に正規化
 */
function normalizeDevice(d) {
  return {
    ...d,
    droneId          : d.thingName,
    displayName      : d.attributes?.displayName ?? d.thingName,
    color            : d.attributes?.color ?? '#3D6EF5',
    operationalStatus: 'available',   // IoT接続状態に関わらず予約可能扱い
    groups           : d.groupNames ?? [],
    model            : d.attributes?.model ?? d.thingTypeName ?? '',
  };
}

function normalizeUser(u) {
  return { ...u, name: u.displayName ?? u.email ?? '' };
}

// ============================================================
// API クライアント
// ============================================================

export const flightApi = {
  list  : (params = {}) => { const q = new URLSearchParams(params).toString(); return authFetch(`/flights${q ? `?${q}` : ''}`); },
  get   : (flightId)       => authFetch(`/flights/${flightId}`),
  create: (body)           => authFetch('/flights', { method: 'POST', body: JSON.stringify(body) }),
  update: (flightId, body) => authFetch(`/flights/${flightId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (flightId)       => authFetch(`/flights/${flightId}`, { method: 'DELETE' }),
};

export const maintenanceApi = {
  list  : (params = {}) => { const q = new URLSearchParams(params).toString(); return authFetch(`/maintenance${q ? `?${q}` : ''}`); },
  alerts: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    try {
      const data = await authFetch(`/maintenance/alerts${q ? `?${q}` : ''}`);
      return { alerts: data.alerts ?? data.items ?? [] };
    } catch { return { alerts: [] }; }
  },
  get   : (maintId)       => authFetch(`/maintenance/${maintId}`),
  create: (body)          => authFetch('/maintenance', { method: 'POST', body: JSON.stringify(body) }),
  update: (maintId, body) => authFetch(`/maintenance/${maintId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (maintId)       => authFetch(`/maintenance/${maintId}`, { method: 'DELETE' }),
};

export const droneApi = {
  // 機体一覧 = IoT Core の Thing 一覧（/devices API）
  list: async () => {
    const data = await authFetch('/devices?maxResults=200');
    const drones = (data.devices ?? []).map(normalizeDevice);
    return { drones, count: drones.length };
  },
  listGroups: async () => {
    try {
      const data = await authFetch('/groups');
      const rawGroups = data.groups ?? data.items ?? [];

      // 各グループの所属機体リストを並列取得
      const groups = await Promise.all(
        rawGroups.map(async (g) => {
          try {
            const t = await authFetch(`/groups/${g.groupName}/things`);
            const things = (t.things ?? t.items ?? []).map(
              item => item.thingName ?? item
            );
            return {
              groupName  : g.groupName,
              displayName: g.groupName,   // IoT Core は displayName なし
              things,
            };
          } catch {
            return { groupName: g.groupName, displayName: g.groupName, things: [] };
          }
        })
      );
      return { groups };
    } catch { return { groups: [] }; }
  },
  get      : async (thingName) => { const d = await authFetch(`/devices/${thingName}`); return normalizeDevice(d); },
  update   : (thingName, body) => authFetch(`/devices/${thingName}`, { method: 'PATCH', body: JSON.stringify(body) }),
  getStatus: (thingName)       => authFetch(`/drones/${thingName}/status`),
};

export const userApi = {
  list: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const data = await authFetch(`/users${q ? `?${q}` : ''}`);
    const users = (data.items ?? data.users ?? []).map(normalizeUser);
    return { users, count: users.length };
  },
  me      : ()             => authFetch('/users/me'),
  get     : (userId)       => authFetch(`/users/${userId}`),
  invite  : (body)         => authFetch('/users/invite', { method: 'POST', body: JSON.stringify(body) }),
  update  : (userId, body) => authFetch(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  register: (body)         => authFetch('/users/register', { method: 'POST', body: JSON.stringify(body) }),
};